import crashlytics from "./crashlytics";
import { CURRENT_ENV } from "./api";

// Track current screen for error context
let currentScreen = "unknown";
let lastAction = "none";

/**
 * Check if analytics should be enabled (production only)
 */
const isAnalyticsEnabled = (): boolean => {
  return CURRENT_ENV.includes("production");
};

/**
 * Initialize global error handlers
 * Call this once in App.tsx on mount
 */
export const initializeErrorHandling = () => {
  if (!isAnalyticsEnabled()) {
    console.log(
      "[Analytics Debug] Error tracking disabled in non-production environment"
    );
    return;
  }

  console.log("✅ Global error handlers initialized");

  // Catch synchronous JavaScript errors
  window.onerror = (message, source, lineno, colno, error) => {
    console.error("[Global Error]", {
      message,
      source,
      lineno,
      colno,
      error,
    });

    // Report to Crashlytics
    crashlytics.recordException(error || new Error(String(message)), {
      error_type: "global_error",
      source_file: source || "unknown",
      line_number: String(lineno || 0),
      column_number: String(colno || 0),
      current_screen: currentScreen,
      last_action: lastAction,
    });

    // Return false to allow default error handling
    return false;
  };

  // Catch unhandled promise rejections
  window.addEventListener("unhandledrejection", (event) => {
    console.error("[Unhandled Promise Rejection]", event.reason);

    // Convert rejection reason to Error if needed
    const error =
      event.reason instanceof Error
        ? event.reason
        : new Error(String(event.reason));

    // Report to Crashlytics
    crashlytics.recordException(error, {
      error_type: "unhandled_rejection",
      current_screen: currentScreen,
      last_action: lastAction,
      promise_reason: String(event.reason),
    });

    // Prevent default console error
    event.preventDefault();
  });
};

/**
 * Set the current screen/page for error context
 * Call this in route changes or screen transitions
 *
 * @example
 * // In a component
 * useEffect(() => {
 *   setCurrentScreen('LoginPage');
 * }, []);
 */
export const setCurrentScreen = (screenName: string) => {
  currentScreen = screenName;
  crashlytics.log(`Screen: ${screenName}`);
  crashlytics.setCustomKey("current_screen", screenName);

  if (!isAnalyticsEnabled()) {
    console.log(`[Analytics Debug] Screen tracked: ${screenName}`);
  }
};

/**
 * Log user actions as breadcrumbs for error context
 * Call this for important user interactions
 *
 * @example
 * logUserAction('clicked_submit_button');
 * logUserAction('started_bluetooth_scan');
 */
export const logUserAction = (action: string) => {
  lastAction = action;
  crashlytics.log(`Action: ${action}`);
  crashlytics.setCustomKey("last_action", action);

  if (!isAnalyticsEnabled()) {
    console.log(`[Analytics Debug] Action tracked: ${action}`);
  }
};

/**
 * Manually report a caught error to Crashlytics
 * Use this in try-catch blocks for critical errors you want tracked
 *
 * @example
 * try {
 *   await criticalOperation();
 * } catch (error) {
 *   reportError(error, { operation: 'criticalOperation', userId: user.id });
 *   // Still show toast to user
 *   showToast('Operation failed');
 * }
 */
export const reportError = (
  error: Error | unknown,
  context?: Record<string, string>
) => {
  const errorObj = error instanceof Error ? error : new Error(String(error));

  console.error("[Reported Error]", errorObj, context);

  crashlytics.recordException(errorObj, {
    error_type: "caught_error",
    current_screen: currentScreen,
    last_action: lastAction,
    ...context,
  });
};

/**
 * Track non-critical errors or warnings
 * These won't trigger alerts but will be logged for analysis
 *
 * @example
 * trackNonCriticalError('Bluetooth scan timeout', { deviceId: 'ABC123' });
 */
export const trackNonCriticalError = (
  message: string,
  context?: Record<string, string>
) => {
  console.warn("[Non-Critical Error]", message, context);

  crashlytics.log(`Non-critical: ${message}`);

  if (context) {
    Object.entries(context).forEach(([key, value]) => {
      crashlytics.setCustomKey(`noncritical_${key}`, value);
    });
  }
};
